import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getPriceTypeLabel, priceTypeEnum, systemConfigKeyEnum } from '../../../../constants';
import { useCreateToolTypeMutation, useUpdateToolTypeMutation } from '../../../../hooks/mutations/useToolTypeMutation';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { ToolType } from '../../../../services/apiSLTP/materials/toolsTypes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown from '../../../ui/DropDown';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    toolType: ToolType | null;
    showEditModal: boolean;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateToolTypeModal: React.FC<ModalProps> = ({ toolType, showEditModal, setShowEditModal, isEditMode = true, refetch }) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();

    const [selectedDesignation, setSelectedDesignation] = useState(toolType?.designation || '');
    const [price, setPrice] = useState(typeof toolType?.price === 'number' ? toolType?.price.toString() : '');
    const [priceType, setPriceType] = useState({
        label: capitalizeFirstLetter(t(getPriceTypeLabel(toolType?.priceType || priceTypeEnum.DAY))),
        value: (toolType?.priceType || priceTypeEnum.DAY).toString(),
    });
    const [selectedCrudStatus, setSelectedCrudStatus] = useState(toolType?.crudStatus || crudStatusEnum.OK);
    const [displayOrder, setDisplayOrder] = useState(typeof toolType?.displayOrder === 'number' ? toolType?.displayOrder.toString() : '');

    const { mutate: mutateToolType } = useUpdateToolTypeMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error updating toolType:', error.message),
    });

    const { mutate: addToolType } = useCreateToolTypeMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error adding toolType:', error.message),
    });

    const handleActiveChange = (isActive: boolean) => {
        setSelectedCrudStatus(isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE);
    };

    const isFormValid = () => {
        return !!(selectedDesignation.length && price.length);
    };

    const handleSubmit = () => {
        if (isEditMode) {
            mutateToolType({
                toolTypeId: toolType?._id || '',
                data: {
                    designation: selectedDesignation,
                    crudStatus: selectedCrudStatus,
                    priceType: parseInt(priceType.value),
                    price: parseFloat(price),
                    displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
                },
            });
        } else {
            if (priceType)
                addToolType({
                    designation: selectedDesignation,
                    crudStatus: selectedCrudStatus,
                    priceType: parseInt(priceType.value),
                    price: parseFloat(price),
                    displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
                });
        }
    };

    return (
        <ModalBody
            title={t('tool')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!isFormValid()}
            onSubmit={handleSubmit}
        >
            <CustomTextInput value={selectedDesignation} onChange={setSelectedDesignation} label={t('name')} required />
            <ToggleLine checked={selectedCrudStatus === crudStatusEnum.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
            <CustomTextInput
                value={price}
                onChange={setPrice}
                label={t('price')}
                errorText={t('error')}
                required
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
            <DropDown
                items={Object.values(priceTypeEnum).map((type) => ({
                    label: capitalizeFirstLetter(t(getPriceTypeLabel(type))),
                    value: type.toString(),
                }))}
                selectedItem={priceType}
                handleChange={(e, value) => {
                    if (value) setPriceType(value);
                }}
                inputLabel={t('pricing')}
                required
            />
            <CustomTextInput value={displayOrder} onChange={setDisplayOrder} label={t('displayOrder')} type="number" />
        </ModalBody>
    );
};

export default UpdateToolTypeModal;
